<template>
  <v-app>
    <v-app-bar app clipped-left clipped-right color="white">
      <v-app-bar-nav-icon v-if="currentClient">
        <v-btn icon light @click="drawer = !drawer">
          <v-icon large>mdi-menu</v-icon>
        </v-btn>
      </v-app-bar-nav-icon>
      <v-app-bar-title>
        <v-img
          contain
          width="200"
          :src="require('@/assets/nexus-logo.png')"
          @click="navigateHome()"
          style="cursor: pointer"
        />
      </v-app-bar-title>
    </v-app-bar>
    <NavigationDrawer v-if="currentClient" v-model="drawer" />
    <CaseNavigator />
    <v-main>
      <v-container fluid :class="containerClass">
        <IntroModal v-model="showIntro" />
        <router-view></router-view>
      </v-container>
    </v-main>
  </v-app>
</template>

<style>
html {
  overflow-y: auto;
}
</style>

<script>
import fetchURLParam from "@/shared/url_params";
import apiOptions from "@/plugins/axios/axios_options";
import { mapGetters, mapActions } from "vuex";
import CaseNavigator from "@/components/CaseNavigator";
import NavigationDrawer from "@/components/NavigationDrawer";
import IntroModal from "./components/IntroModal";

export default {
  name: "App",
  components: {
    CaseNavigator,
    NavigationDrawer,
    IntroModal,
  },
  data() {
    return {
      fullscreen: false,
      drawer: false,
      notRegistered: false,
      showIntro: false,
    };
  },
  watch: {
    $route(to) {
      if (to.name === "nxcHomePage" || to.name === "nxcTermsPage") {
        this.fullscreen = true;
      } else {
        this.fullscreen = false;
      }
      if (!this.notRegistered) {
        return;
      }
      if (to.name === "clientRegistration") {
        return;
      }
      this.$router.push({ name: "clientRegistration" });
    },
    currentClient(value, oldValue) {
      if (!value) {
        return;
      }
      if (oldValue && oldValue.uid !== value.uid) {
        this.setCollection(null);
      }
      this.fetchPracticeData();
    },
    reloadTrigger(value) {
      if (!value) {
        return;
      }
      this.fetchPracticeData().then(() => {
        this.reloadComplete();
      });
    },
  },
  computed: {
    ...mapGetters(["isLoggedIn", "reloadTrigger", "clients", "currentClient"]),
    containerClass() {
      if (this.fullscreen) {
        return "fill-height pa-0";
      }
      return "fill-height";
    },
    currentYear() {
      return new Date().getFullYear();
    },
  },
  methods: {
    ...mapActions([
      "setAccessToken",
      "reloadComplete",
      "setClients",
      "setCurrentClient",
      "setActiveCases",
      "setArchivedCases",
      "setApprovalRequiredCases",
      "setUpgradableCases",
      "setIncompleteSubmissions",
      "setInternationalToothConvention",
      "setCollection",
    ]),
    initializeApplication(accessToken) {
      this.setAccessToken(accessToken);
      this.$axios.defaults.headers.Authorization = `Bearer ${accessToken}`;
      this.$axios
        .get("/ordering/clients/")
        .then((response) => {
          this.setClients(response.data);
          if (this.clients.length > 1) {
            const currentClientUID = this.$cookies.get(
              "osteonCurrentClientUID",
            );
            if (currentClientUID) {
              const client = this.clients.find(
                (client) => client.uid === currentClientUID,
              );
              if (client) {
                this.setCurrentClient(client);
              }
            }
          }
          if (!this.currentClient) {
            this.$router.push({ name: "practiceSelector" });
          } else if (this.$route.name === "authCallback") {
            this.$router.push({ name: "homePage" });
          }
        })
        .catch(() => {
          if (this.$route.name !== "authCallback") {
            this.$axios.defaults.headers.Authorization = null;
            this.$cookies
              .keys()
              .forEach((cookie) => this.$cookies.remove(cookie));
            return this.redirectToID();
          }
          this.notRegistered = true;
          if (this.$route.name === "clientRegistration") {
            return;
          }
          this.$router.push({ name: "clientRegistration" });
        });
    },
    redirectToID() {
      window.location = `${apiOptions.authURL}?client_id=${
        apiOptions.clientID
      }&response_type=token&redirect_uri=${encodeURIComponent(
        apiOptions.redirectURI,
      )}`;
    },
    navigateHome() {
      if (this.$route.name === "homePage") {
        return;
      }
      this.$router.push({ name: "homePage" });
    },
    changePractice() {
      this.$router.push({ name: "practiceSelector" });
    },
    isCurrentClient(clientUID) {
      return !!this.currentClient && this.currentClient.uid === clientUID;
    },
    async fetchPracticeData() {
      return new Promise((resolve) => {
        const requests = [
          this.fetchApprovalRequiredCases(),
          this.fetchUpgradableCases(),
          this.fetchActiveCases(),
          this.fetchArchivedCases(),
          this.fetchSubmissions(),
        ];
        Promise.all(requests).then(() => resolve());
      });
    },
    async fetchActiveCases() {
      const clientUID = this.currentClient.uid;
      return new Promise((resolve, reject) => {
        this.$axios
          .get(`/${clientUID}/active/`)
          .then((response) => {
            if (this.isCurrentClient(clientUID)) {
              this.setActiveCases(response.data);
            }
            resolve();
          })
          .catch(() => {
            reject();
          });
      });
    },
    async fetchArchivedCases() {
      const clientUID = this.currentClient.uid;
      return new Promise((resolve, reject) => {
        this.$axios
          .get(`/${clientUID}/archived/`)
          .then((response) => {
            if (this.isCurrentClient(clientUID)) {
              this.setArchivedCases(response.data);
            }
            resolve();
          })
          .catch(() => {
            reject();
          });
      });
    },
    async fetchApprovalRequiredCases() {
      const clientUID = this.currentClient.uid;
      return new Promise((resolve, reject) => {
        this.$axios
          .get(`/${clientUID}/approvals/`)
          .then((response) => {
            if (this.isCurrentClient(clientUID)) {
              this.setApprovalRequiredCases(response.data);
            }
            resolve();
          })
          .catch(() => {
            reject();
          });
      });
    },
    async fetchUpgradableCases() {
      const clientUID = this.currentClient.uid;
      return new Promise((resolve, reject) => {
        this.$axios
          .get(`/${clientUID}/upgradable/`)
          .then((response) => {
            if (this.isCurrentClient(clientUID)) {
              this.setUpgradableCases(response.data);
            }
            resolve();
          })
          .catch(() => {
            reject();
          });
      });
    },
    async fetchSubmissions() {
      const clientUID = this.currentClient.uid;
      return new Promise((resolve, reject) => {
        this.$axios
          .get(`/ordering/submissions/${clientUID}/`)
          .then((response) => {
            if (this.isCurrentClient(clientUID)) {
              this.setIncompleteSubmissions(response.data);
            }
            resolve();
          })
          .catch(() => {
            reject();
          });
      });
    },
  },
  beforeMount() {
    let accessToken = this.$cookies.get("osteonAccessToken");
    if (accessToken) {
      this.initializeApplication(accessToken);
    } else {
      accessToken = fetchURLParam("access_token");
      const expiresInSecs = fetchURLParam("expires_in");
      const expiresInMSecs = expiresInSecs * 1000;
      const expiresAt = new Date(new Date().getTime() + expiresInMSecs);
      if (accessToken && expiresAt) {
        this.$cookies.config(expiresAt);
        this.$cookies.set("osteonAccessToken", accessToken);
        this.initializeApplication(accessToken);
      } else {
        this.redirectToID();
      }
    }
  },
  mounted() {
    const existingConvention = this.$cookies.get(
      "internationalToothConvention",
    );
    if (existingConvention) {
      this.setInternationalToothConvention(existingConvention);
    }
    // if (!this.$cookies.get("introWatched")) {
    //   setTimeout(() => {
    //     this.showIntro = true;
    //   }, 1000);
    // }
  },
};
</script>
